/**
 * A stored model value carries three things in one string:
 *
 *   [<provider>:]<model>[@<effort>]
 *
 * - `provider:` — a non-default CLI (the scheme the backend failover chain
 *   already uses for cross-provider runs), e.g. `codex:`.
 * - `@effort`   — a reasoning-effort level, translated to `--effort` (claude)
 *   or `-c model_reasoning_effort` (codex) at dispatch.
 *
 * Both prefix and suffix are optional, so a bare `claude-opus-4-8` and the
 * Codex no-model sentinel `codex:` both decode cleanly. The effort suffix is
 * recognized only when it's a known level, so a model id that happens to
 * contain `@` is never mangled.
 */

const EFFORT_LEVELS = new Set(["low", "medium", "high", "xhigh", "max"]);

export interface DecodedModel {
  provider: string; // "" = use default provider
  model: string; // "" = use default model (or the Codex no-model sentinel)
  effort: string; // "" = use the CLI's own default effort
}

export function decodeModel(raw: string | null | undefined): DecodedModel {
  const v = (raw ?? "").trim();
  let provider = "";
  let rest = v;
  const colon = rest.indexOf(":");
  if (colon >= 0) {
    provider = rest.slice(0, colon);
    rest = rest.slice(colon + 1);
  }
  let effort = "";
  const at = rest.lastIndexOf("@");
  if (at >= 0 && EFFORT_LEVELS.has(rest.slice(at + 1).toLowerCase())) {
    effort = rest.slice(at + 1).toLowerCase();
    rest = rest.slice(0, at);
  }
  return { provider, model: rest, effort };
}

export function encodeModel(d: DecodedModel): string {
  const base = d.model ?? "";
  // Never attach effort to an empty model (the no-model sentinel).
  const withEffort = d.effort && base ? `${base}@${d.effort}` : base;
  return d.provider ? `${d.provider}:${withEffort}` : withEffort;
}
